import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCapsules, faPills, faPlus, faClipboardList, faCheckCircle,
  faClock, faCircleCheck, faEdit, faTrash, faHeartPulse,
  faSyringe, faDroplet, faLungs, faPrescriptionBottle,
  faCubes, faBoxOpen, faCalendarDays, faUserDoctor,
  faClipboardCheck, faListCheck, faPen, faExclamationTriangle,
  faBell, faWind, faWandMagicSparkles, faChevronDown, faChevronUp,
  faSpinner, faRobot, faCloudArrowUp, faCircleInfo,
  faTriangleExclamation, faBookMedical, faCheck, faCheckDouble,
  faFileUpload
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import './MedicineReminder.css';
import medicineReminderService, { MedicineReminder as MedicineReminderType, MedicineReminderPayload } from '../../services/medicineReminderService';
import BackButton from '../../components/BackButton/BackButton';
import { useProfile } from '../../context/ProfileContext';

// Toast notification component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`mr-toast mr-toast-${type}`}>
      <span className="mr-toast-icon">
        {type === 'success' ? '\u2713' : type === 'error' ? '\u2715' : '\u2139'}
      </span>
      <span className="mr-toast-message">{message}</span>
      <button className="mr-toast-close" onClick={onClose}>{'\u00D7'}</button>
    </div>
  );
};

interface StatItem {
  icon: IconDefinition;
  color: string;
  value: string | number;
  label: string;
}

// Medicine type icon mapping
const getMedicineTypeIcon = (type: string): IconDefinition => {
  switch (type) {
    case 'tablet': return faPills;
    case 'capsule': return faCapsules;
    case 'syrup': return faPrescriptionBottle;
    case 'drops': return faDroplet;
    case 'injection': return faSyringe;
    case 'inhaler': return faLungs;
    case 'cream':
    case 'gel':
    case 'ointment': return faPrescriptionBottle;
    case 'spray': return faWind;
    default: return faPills;
  }
};

// Medicine type form options
const getMedicineTypeOptions = (t: (key: string) => string): { label: string; value: string; icon: IconDefinition }[] => [
  { label: t('services:medicineReminder.syrup'), value: 'syrup', icon: faPrescriptionBottle },
  { label: t('services:medicineReminder.tablet'), value: 'tablet', icon: faPills },
  { label: t('services:medicineReminder.drops'), value: 'drops', icon: faDroplet },
  { label: t('services:medicineReminder.inhalers'), value: 'inhalers', icon: faLungs },
  { label: t('services:medicineReminder.injections'), value: 'injections', icon: faSyringe },
  { label: t('services:medicineReminder.creamsGel'), value: 'cream_gel', icon: faPrescriptionBottle },
];


const MedicineReminder: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState<'active' | 'upcoming' | 'completed' | 'all' | 'add'>('all');

  // Medicine Reminder State
  const [reminders, setReminders] = useState<MedicineReminderType[]>([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);
  const [editingReminder, setEditingReminder] = useState<MedicineReminderType | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  }, []);

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  // Type definitions (moved up for scan logic)
  type MedicineType = 'tablet' | 'capsule' | 'syrup' | 'drops' | 'injection' | 'inhaler' | 'cream' | 'gel' | 'ointment' | 'powder' | 'spray' | 'patch' | 'other';
  type DosageUnit = 'tablet' | 'capsule' | 'ml' | 'drops' | 'mg' | 'puff' | 'application' | 'unit';
  type IntakeFrequency = 'once' | 'twice' | 'thrice' | 'four_times' | 'five_times' | 'six_times';
  type MealRelation = 'before_meal' | 'after_meal' | 'with_meal' | 'none';

  const frequencyToSlotCount: Record<IntakeFrequency, number> = {
    once: 1,
    twice: 2,
    thrice: 3,
    four_times: 4,
    five_times: 5,
    six_times: 6,
  };

  const defaultTimesForSlots = (count: number): { time: string; meal_relation: MealRelation }[] => {
    const defaults = ['08:00', '13:00', '18:00', '22:00', '10:00', '16:00'];
    return Array.from({ length: count }, (_, i) => ({
      time: defaults[i] || '08:00',
      meal_relation: 'after_meal' as MealRelation,
    }));
  };

  // Prescription scan state
  interface DrugInfo {
    generic_name: string;
    category: string;
    used_for: string;
    common_side_effects: string[];
    important_warnings: string[];
    how_to_take: string;
    interactions: string[];
  }

  interface ExtractedMedicine {
    medicine_name: string;
    medicine_type: string;
    dosage_value: number;
    dosage_unit: string;
    frequency: string;
    duration_value: number;
    duration_unit: string;
    meal_relation: string;
    schedule_times: string[];
    notes: string;
    drug_info: DrugInfo;
  }

  const [scanExpanded, setScanExpanded] = useState(false);
  const [scanStep, setScanStep] = useState<'idle' | 'scanning' | 'extracting' | 'done' | 'error'>('idle');
  const [scanProgress, setScanProgress] = useState(0);
  const [scanError, setScanError] = useState('');
  const [prescriptionFile, setPrescriptionFile] = useState<File | null>(null);
  const [extractedMedicines, setExtractedMedicines] = useState<ExtractedMedicine[]>([]);
  const [extractedDoctorName, setExtractedDoctorName] = useState('');
  const [selectedMedicineIndices, setSelectedMedicineIndices] = useState<Set<number>>(new Set());
  const [expandedDrugInfo, setExpandedDrugInfo] = useState<Set<number>>(new Set());
  const [autoFilledFromScan, setAutoFilledFromScan] = useState(false);
  const [showAddAllConfirm, setShowAddAllConfirm] = useState(false);
  const [addingAllReminders, setAddingAllReminders] = useState(false);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [defaultMealRelation, setDefaultMealRelation] = useState<MealRelation>('after_meal');

  // Handle prescription file upload
  const handlePrescriptionUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPrescriptionFile(file);
    setScanStep('scanning');
    setScanProgress(0);
    setExtractedMedicines([]);
    setSelectedMedicineIndices(new Set());
    setExpandedDrugInfo(new Set());

    // Simulate scanning progress
    const progressInterval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 85) {
          clearInterval(progressInterval);
          return 85;
        }
        return prev + Math.random() * 15;
      });
    }, 400);

    try {
      // Switch to extracting phase after a brief scan
      setTimeout(() => {
        setScanStep('extracting');
      }, 1500);

      const result = await medicineReminderService.extractFromPrescription(file);

      clearInterval(progressInterval);
      setScanProgress(100);

      if (result && result.medicines && result.medicines.length > 0) {
        setExtractedMedicines(result.medicines);
        setExtractedDoctorName(result.doctor_name || '');
        setScanStep('done');
      } else {
        setScanError('No medicines found in the prescription. Please try a clearer image.');
        setScanStep('error');
      }
    } catch (error: any) {
      clearInterval(progressInterval);
      setScanError(error?.response?.data?.message || 'Failed to extract prescription. Please try again.');
      setScanStep('error');
    }

    // Reset file input
    e.target.value = '';
  };

  // Toggle medicine selection
  const toggleMedicineSelection = (index: number) => {
    setSelectedMedicineIndices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Toggle drug info expansion
  const toggleDrugInfo = (index: number) => {
    setExpandedDrugInfo(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Map frequency string to IntakeFrequency
  const mapFrequencyToIntake = (freq: string): IntakeFrequency => {
    const lower = freq.toLowerCase();
    if (lower.includes('once') || lower.includes('1') || lower.includes('one')) return 'once';
    if (lower.includes('twice') || lower.includes('2') || lower.includes('two')) return 'twice';
    if (lower.includes('thrice') || lower.includes('3') || lower.includes('three')) return 'thrice';
    if (lower.includes('four') || lower.includes('4')) return 'four_times';
    if (lower.includes('five') || lower.includes('5')) return 'five_times';
    if (lower.includes('six') || lower.includes('6')) return 'six_times';
    return 'once';
  };

  // Map meal relation string
  const mapMealRelation = (relation: string): MealRelation => {
    const lower = relation.toLowerCase();
    if (lower.includes('before')) return 'before_meal';
    if (lower.includes('after')) return 'after_meal';
    if (lower.includes('with')) return 'with_meal';
    return 'none';
  };

  // Map medicine type string
  const mapMedicineType = (type: string): MedicineType => {
    const lower = type.toLowerCase();
    if (lower.includes('tablet') || lower.includes('tab')) return 'tablet';
    if (lower.includes('capsule') || lower.includes('cap')) return 'capsule';
    if (lower.includes('syrup') || lower.includes('liquid') || lower.includes('suspension')) return 'syrup';
    if (lower.includes('drop')) return 'drops';
    if (lower.includes('injection') || lower.includes('inject')) return 'injection';
    if (lower.includes('inhaler')) return 'inhaler';
    if (lower.includes('cream')) return 'cream';
    if (lower.includes('gel')) return 'gel';
    if (lower.includes('ointment')) return 'ointment';
    if (lower.includes('spray')) return 'spray';
    return 'tablet';
  };

  // Map dosage unit string
  const mapDosageUnit = (unit: string): DosageUnit => {
    const lower = unit.toLowerCase();
    if (lower.includes('tablet') || lower.includes('tab')) return 'tablet';
    if (lower.includes('capsule') || lower.includes('cap')) return 'capsule';
    if (lower.includes('ml')) return 'ml';
    if (lower.includes('drop')) return 'drops';
    if (lower.includes('mg')) return 'mg';
    if (lower.includes('puff')) return 'puff';
    return 'tablet';
  };

  // Auto-fill form from a selected medicine
  const handleAutoFillFromMedicine = (med: ExtractedMedicine) => {
    const intakeFreq = mapFrequencyToIntake(med.frequency);
    const mealRel = mapMealRelation(med.meal_relation);
    const medType = mapMedicineType(med.medicine_type);
    const dosUnit = mapDosageUnit(med.dosage_unit);
    const slotCount = frequencyToSlotCount[intakeFreq];

    // Build schedule times from extracted data or defaults
    let scheduleTimes: { time: string; meal_relation: MealRelation }[] = [];
    if (med.schedule_times && med.schedule_times.length > 0) {
      scheduleTimes = med.schedule_times.slice(0, slotCount).map(t => ({
        time: t,
        meal_relation: mealRel,
      }));
      // Pad with defaults if needed
      while (scheduleTimes.length < slotCount) {
        const defaults = defaultTimesForSlots(slotCount);
        scheduleTimes.push({ ...defaults[scheduleTimes.length], meal_relation: mealRel });
      }
    } else {
      scheduleTimes = defaultTimesForSlots(slotCount).map(s => ({
        ...s,
        meal_relation: mealRel,
      }));
    }

    // Calculate start and end dates
    const today = new Date();
    const startDate = today.toISOString().split('T')[0];
    const durationDays = med.duration_unit === 'week'
      ? med.duration_value * 7
      : med.duration_unit === 'month'
        ? med.duration_value * 30
        : med.duration_value;
    const endDateObj = new Date(today);
    endDateObj.setDate(endDateObj.getDate() + durationDays);
    const endDate = endDateObj.toISOString().split('T')[0];

    setFormData({
      medicineName: med.medicine_name,
      medicineType: medType,
      startDate,
      endDate,
      duration: med.duration_value || 1,
      durationUnit: (med.duration_unit as 'day' | 'week' | 'month') || 'day',
      dosageValue: med.dosage_value || 1,
      dosageUnit: dosUnit,
      frequencyType: 'fixed_times',
      intakeFrequency: intakeFreq,
      intervalType: '',
      doctorName: extractedDoctorName || '',
      appointmentReminderDate: '',
      currentInventory: 0,
      remindWhenInventory: 0,
      scheduleTimes,
    });

    setAutoFilledFromScan(true);
  };

  // Handle "Add Selected" button
  const handleAddSelectedMedicine = () => {
    if (selectedMedicineIndices.size === 0) {
      showToast('Please select at least one medicine.', 'error');
      return;
    }
    // If only one selected, auto-fill the form
    if (selectedMedicineIndices.size === 1) {
      const idx = Array.from(selectedMedicineIndices)[0];
      handleAutoFillFromMedicine(extractedMedicines[idx]);
      showToast('Form auto-filled from prescription. Please verify before saving.', 'info');
    } else {
      // Multiple selected - use the first one for form, suggest "Add All" for the rest
      const idx = Array.from(selectedMedicineIndices)[0];
      handleAutoFillFromMedicine(extractedMedicines[idx]);
      showToast(`Form filled with first selected medicine. Use "Add All as Reminders" for bulk creation.`, 'info');
    }
  };

  // Handle "Add All as Reminders"
  const handleAddAllAsReminders = async () => {
    const medicinesToAdd = selectedMedicineIndices.size > 0
      ? Array.from(selectedMedicineIndices).map(i => extractedMedicines[i])
      : extractedMedicines;

    setAddingAllReminders(true);
    let successCount = 0;
    let failCount = 0;

    for (const med of medicinesToAdd) {
      try {
        const intakeFreq = mapFrequencyToIntake(med.frequency);
        const mealRel = mapMealRelation(med.meal_relation);
        const medType = mapMedicineType(med.medicine_type);
        const dosUnit = mapDosageUnit(med.dosage_unit);
        const slotCount = frequencyToSlotCount[intakeFreq];

        let scheduleTimes: { time: string; meal_relation: string }[] = [];
        if (med.schedule_times && med.schedule_times.length > 0) {
          scheduleTimes = med.schedule_times.slice(0, slotCount).map(t => ({ time: t, meal_relation: mealRel }));
          while (scheduleTimes.length < slotCount) {
            const defaults = defaultTimesForSlots(slotCount);
            scheduleTimes.push({ time: defaults[scheduleTimes.length].time, meal_relation: mealRel });
          }
        } else {
          scheduleTimes = defaultTimesForSlots(slotCount).map(s => ({ time: s.time, meal_relation: mealRel }));
        }

        const today = new Date();
        const startDate = today.toISOString().split('T')[0];
        const durationDays = med.duration_unit === 'week' ? med.duration_value * 7 : med.duration_unit === 'month' ? med.duration_value * 30 : med.duration_value;
        const endDateObj = new Date(today);
        endDateObj.setDate(endDateObj.getDate() + (durationDays || 7));
        const endDate = endDateObj.toISOString().split('T')[0];

        const payload: MedicineReminderPayload = {
          medicine_name: med.medicine_name,
          medicine_type: medType,
          duration_value: med.duration_value || 1,
          duration_unit: med.duration_unit || 'day',
          start_date: startDate,
          end_date: endDate,
          frequency_type: 'fixed_times',
          intake_frequency: intakeFreq,
          dosage_value: med.dosage_value || 1,
          dosage_unit: dosUnit,
          doctor_name: extractedDoctorName || undefined,
          schedule_times: scheduleTimes,
        };

        await medicineReminderService.createReminder(payload, prescriptionFile ? [prescriptionFile] : undefined);
        successCount++;
      } catch (error) {
        console.error(`Failed to create reminder for ${med.medicine_name}:`, error);
        failCount++;
      }
    }

    setAddingAllReminders(false);
    setShowAddAllConfirm(false);

    if (successCount > 0) {
      showToast(`Successfully created ${successCount} reminder${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}.`, 'success');
      fetchReminders();
      resetScan();
      resetForm();
      setActiveTab('all');
    } else {
      showToast('Failed to create reminders. Please try again.', 'error');
    }
  };

  // Reset scan state
  const resetScan = () => {
    setScanStep('idle');
    setScanProgress(0);
    setScanError('');
    setPrescriptionFile(null);
    setExtractedMedicines([]);
    setExtractedDoctorName('');
    setSelectedMedicineIndices(new Set());
    setExpandedDrugInfo(new Set());
    setAutoFilledFromScan(false);
  };

  // Helper to get reminder status
  const getReminderStatus = (reminder: MedicineReminderType): 'active' | 'upcoming' | 'completed' => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(reminder.start_date);
    const endDate = new Date(reminder.end_date);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (today > endDate) return 'completed';
    if (today < startDate) return 'upcoming';
    return 'active';
  };

  // Form State
  const [formData, setFormData] = useState<{
    medicineName: string;
    medicineType: MedicineType;
    startDate: string;
    endDate: string;
    duration: number;
    durationUnit: 'day' | 'week' | 'month';
    dosageValue: number;
    dosageUnit: DosageUnit;
    frequencyType: 'fixed_times' | 'interval';
    intakeFrequency: IntakeFrequency;
    intervalType: string;
    doctorName: string;
    appointmentReminderDate: string;
    currentInventory: number;
    remindWhenInventory: number;
    scheduleTimes: { time: string; meal_relation: MealRelation }[];
  }>({
    medicineName: '',
    medicineType: 'tablet',
    startDate: '',
    endDate: '',
    duration: 1,
    durationUnit: 'day',
    dosageValue: 1,
    dosageUnit: 'tablet',
    frequencyType: 'fixed_times',
    intakeFrequency: 'once',
    intervalType: '',
    doctorName: '',
    appointmentReminderDate: '',
    currentInventory: 0,
    remindWhenInventory: 0,
    scheduleTimes: [{ time: '08:00', meal_relation: 'after_meal' as MealRelation }],
  });

  // Auto-adjust schedule time slots when intake frequency changes
  const handleIntakeFrequencyChange = (freq: IntakeFrequency) => {
    const targetCount = frequencyToSlotCount[freq];
    let newSlots = [...formData.scheduleTimes];
    if (newSlots.length < targetCount) {
      const defaults = defaultTimesForSlots(targetCount);
      while (newSlots.length < targetCount) {
        newSlots.push(defaults[newSlots.length]);
      }
    } else if (newSlots.length > targetCount) {
      newSlots = newSlots.slice(0, targetCount);
    }
    setFormData({ ...formData, intakeFrequency: freq, scheduleTimes: newSlots });
  };

  const handleScheduleTimeChange = (index: number, field: 'time' | 'meal_relation', value: string) => {
    const updated = formData.scheduleTimes.map((slot, i) =>
      i === index ? { ...slot, [field]: value } : slot
    );
    setFormData({ ...formData, scheduleTimes: updated });
  };

  const handleAddTimeSlot = () => {
    const defaults = defaultTimesForSlots(formData.scheduleTimes.length + 1);
    setFormData({
      ...formData,
      scheduleTimes: [...formData.scheduleTimes, defaults[defaults.length - 1]],
    });
  };

  const handleDefaultMealRelationChange = (relation: MealRelation) => {
    setDefaultMealRelation(relation);
    const updatedSlots = formData.scheduleTimes.map(slot => ({
      ...slot,
      meal_relation: relation,
    }));
    setFormData({ ...formData, scheduleTimes: updatedSlots });
  };

  const handleRemoveTimeSlot = (index: number) => {
    if (formData.scheduleTimes.length <= 1) return;
    setFormData({
      ...formData,
      scheduleTimes: formData.scheduleTimes.filter((_, i) => i !== index),
    });
  };

  // Fetch existing reminders on mount and when active profile changes
  useEffect(() => {
    fetchReminders();
  }, [activeProfile]);

  // Handle URL section parameter
  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'reminders') {
      setActiveTab('all');
    }
  }, [searchParams]);

  const fetchReminders = async () => {
    try {
      setLoadingReminders(true);
      const data = await medicineReminderService.getReminders();
      setReminders(data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleSaveReminder = async () => {
    if (!formData.medicineName || !formData.startDate || !formData.endDate) {
      showToast('Please fill in required fields: Medicine Name, Start Date, End Date', 'error');
      return;
    }

    if (!editingReminder) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(formData.startDate);
      start.setHours(0, 0, 0, 0);
      if (start.getTime() < today.getTime()) {
        showToast(
          'Reminders cannot be created for a past date. Please choose today or a future date.',
          'error',
        );
        return;
      }
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end.getTime() < start.getTime()) {
        showToast('End Date cannot be before Start Date.', 'error');
        return;
      }
    }

    try {
      setSavingReminder(true);
      const payload: MedicineReminderPayload = {
        medicine_name: formData.medicineName,
        medicine_type: formData.medicineType,
        duration_value: formData.duration,
        duration_unit: formData.durationUnit,
        start_date: formData.startDate,
        end_date: formData.endDate,
        frequency_type: formData.frequencyType,
        intake_frequency: formData.intakeFrequency,
        dosage_value: formData.dosageValue,
        dosage_unit: formData.dosageUnit,
        doctor_name: formData.doctorName || undefined,
        appointment_reminder_date: formData.appointmentReminderDate || undefined,
        current_inventory: formData.currentInventory || undefined,
        remind_when_inventory: formData.remindWhenInventory || undefined,
        schedule_times: formData.scheduleTimes,
      };

      // Attach prescription file if auto-filled from scan
      const docs = (autoFilledFromScan && prescriptionFile) ? [prescriptionFile] : undefined;

      if (editingReminder) {
        await medicineReminderService.updateReminder(editingReminder.id, payload, docs);
        showToast('Reminder updated successfully!', 'success');
      } else {
        await medicineReminderService.createReminder(payload, docs);
        showToast('Reminder created successfully!', 'success');
      }

      // Reset form and refresh list
      resetForm();
      resetScan();
      fetchReminders();
      setActiveTab('all');
    } catch (error) {
      console.error('Error saving reminder:', error);
      // Surface the actual backend validation message instead of a generic one
      // (e.g. "End date cannot be before start date.") so the user knows why it failed.
      let msg = 'Failed to save reminder. Please try again.';
      const data = (error as { response?: { data?: unknown } })?.response?.data;
      if (typeof data === 'string') {
        msg = data;
      } else if (data && typeof data === 'object') {
        const d = data as Record<string, unknown>;
        const detail = d.detail ?? d.error;
        if (typeof detail === 'string') {
          msg = detail;
        } else {
          const first = Object.entries(d)[0];
          if (first) {
            const [, val] = first;
            const text = Array.isArray(val) ? String(val[0]) : String(val);
            if (text) msg = text;
          }
        }
      }
      showToast(msg, 'error');
    } finally {
      setSavingReminder(false);
    }
  };

  const resetForm = () => {
    setFormData({
      medicineName: '',
      medicineType: 'tablet' as MedicineType,
      startDate: '',
      endDate: '',
      duration: 1,
      durationUnit: 'day',
      dosageValue: 1,
      dosageUnit: 'tablet' as DosageUnit,
      frequencyType: 'fixed_times',
      intakeFrequency: 'once' as IntakeFrequency,
      intervalType: '',
      doctorName: '',
      appointmentReminderDate: '',
      currentInventory: 0,
      remindWhenInventory: 0,
      scheduleTimes: defaultTimesForSlots(1),
    });
    setEditingReminder(null);
    resetScan();
  };

  const handleEditReminder = (reminder: MedicineReminderType) => {
    setFormData({
      medicineName: reminder.medicine_name,
      medicineType: reminder.medicine_type as MedicineType,
      startDate: reminder.start_date,
      endDate: reminder.end_date,
      duration: reminder.duration_value,
      durationUnit: reminder.duration_unit,
      dosageValue: reminder.dosage_value,
      dosageUnit: reminder.dosage_unit as DosageUnit,
      frequencyType: reminder.frequency_type,
      intakeFrequency: (reminder.intake_frequency || 'once') as IntakeFrequency,
      intervalType: reminder.interval_type || '',
      doctorName: reminder.doctor_name || '',
      appointmentReminderDate: reminder.appointment_reminder_date || '',
      currentInventory: reminder.current_inventory || 0,
      remindWhenInventory: reminder.remind_when_inventory || 0,
      scheduleTimes: reminder.schedule_times.map(st => ({ time: st.time, meal_relation: st.meal_relation as MealRelation })),
    });
    setEditingReminder(reminder);
    setActiveTab('add');
  };

  const handleDeleteReminder = async (id: number) => {
    try {
      await medicineReminderService.deleteReminder(id);
      fetchReminders();
      showToast('Reminder deleted successfully!', 'success');
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting reminder:', error);
      showToast('Failed to delete reminder. Please try again.', 'error');
    }
  };

  // Computed stats
  const stats = useMemo(() => {
    const active = reminders.filter(r => getReminderStatus(r) === 'active').length;
    const upcoming = reminders.filter(r => getReminderStatus(r) === 'upcoming').length;
    const completed = reminders.filter(r => getReminderStatus(r) === 'completed').length;
    return { active, upcoming, completed, total: reminders.length };
  }, [reminders]);

  // Stat cards config
  const statItems: StatItem[] = [
    { icon: faClipboardList, color: '#06b6d4', value: stats.total, label: t('services:medicineReminder.totalMedicines') },
    { icon: faCheckCircle, color: '#10b981', value: stats.active, label: t('services:medicineReminder.active') },
    { icon: faClock, color: '#f59e0b', value: stats.upcoming, label: t('services:medicineReminder.upcoming') },
    { icon: faCircleCheck, color: '#8b5cf6', value: stats.completed, label: t('services:medicineReminder.completed') },
  ];

  // Filtered reminders by tab
  const filteredReminders = useMemo(() => {
    if (activeTab === 'active') return reminders.filter(r => getReminderStatus(r) === 'active');
    if (activeTab === 'upcoming') return reminders.filter(r => getReminderStatus(r) === 'upcoming');
    if (activeTab === 'completed') return reminders.filter(r => getReminderStatus(r) === 'completed');
    if (activeTab === 'all') return reminders;
    return reminders;
  }, [activeTab, reminders]);

  const tabs: { key: typeof activeTab; label: string; icon: IconDefinition; count?: number }[] = [
    { key: 'active', label: t('services:medicineReminder.active'), icon: faHeartPulse, count: stats.active },
    { key: 'upcoming', label: t('services:medicineReminder.upcoming'), icon: faClock, count: stats.upcoming },
    { key: 'completed', label: t('services:medicineReminder.completed'), icon: faCircleCheck, count: stats.completed },
    { key: 'all', label: t('services:medicineReminder.allReminders'), icon: faClipboardList, count: stats.total },
    { key: 'add', label: editingReminder ? t('services:medicineReminder.editReminder') : t('services:medicineReminder.addNew'), icon: faPlus, count: undefined },
  ];

  const tabHeaderConfig: Record<string, { title: string; icon: IconDefinition; desc: string; className: string }> = {
    active: {
      title: t('services:medicineReminder.activeReminders'),
      icon: faHeartPulse,
      desc: t('services:medicineReminder.activeDesc'),
      className: 'mr-tab-header-active',
    },
    upcoming: {
      title: t('services:medicineReminder.upcomingReminders'),
      icon: faClock,
      desc: t('services:medicineReminder.upcomingDesc'),
      className: 'mr-tab-header-upcoming',
    },
    completed: {
      title: t('services:medicineReminder.completedReminders'),
      icon: faCircleCheck,
      desc: t('services:medicineReminder.completedDesc'),
      className: 'mr-tab-header-completed',
    },
    all: {
      title: t('services:medicineReminder.allMedicineReminders'),
      icon: faClipboardList,
      desc: t('services:medicineReminder.allDesc'),
      className: 'mr-tab-header-all',
    },
    add: {
      title: editingReminder ? t('services:medicineReminder.editMedicineReminder') : t('services:medicineReminder.addMedicineReminder'),
      icon: editingReminder ? faPen : faPlus,
      desc: editingReminder ? t('services:medicineReminder.editDesc') : t('services:medicineReminder.addDesc'),
      className: 'mr-tab-header-add',
    },
  };

  const currentHeader = tabHeaderConfig[activeTab];

  const getCardAccentClass = (status: string) => {
    if (status === 'active') return 'mr-card-active';
    if (status === 'upcoming') return 'mr-card-upcoming';
    if (status === 'completed') return 'mr-card-completed';
    return '';
  };

  // Render reminder cards
  const renderReminderCards = () => {
    if (loadingReminders) {
      return (
        <div className="mr-loading">
          <div className="mr-loading-spinner" />
          <p className="mr-loading-text">{t('services:medicineReminder.loadingReminders')}</p>
        </div>
      );
    }

    if (filteredReminders.length === 0) {
      return (
        <div className="mr-empty-state">
          <div className="mr-empty-icon">
            <FontAwesomeIcon icon={faCapsules} />
          </div>
          <h3>{t('services:medicineReminder.noRemindersYet', { status: activeTab === 'all' ? '' : activeTab })}</h3>
          <p>
            {activeTab === 'all'
              ? t('services:medicineReminder.noRemindersAllDesc')
              : t('services:medicineReminder.noRemindersStatusDesc', { status: activeTab })}
          </p>
          <button
            className="mr-btn mr-btn-primary"
            onClick={() => { resetForm(); setActiveTab('add'); }}
          >
            <FontAwesomeIcon icon={faPlus} /> {t('services:medicineReminder.addFirstReminder')}
          </button>
        </div>
      );
    }

    return (
      <div className="mr-reminders-grid">
        {filteredReminders.map((reminder, index) => {
          const status = getReminderStatus(reminder);
          return (
            <div
              key={reminder.id}
              className={`mr-reminder-card ${getCardAccentClass(status)}`}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Status Badge */}
              <div className={`mr-status-badge mr-status-${status}`}>
                {status === 'active' && (
                  <><FontAwesomeIcon icon={faCheckCircle} className="mr-status-icon" /> {t('services:medicineReminder.active')}</>
                )}
                {status === 'upcoming' && (
                  <><FontAwesomeIcon icon={faClock} className="mr-status-icon" /> {t('services:medicineReminder.upcoming')}</>
                )}
                {status === 'completed' && (
                  <><FontAwesomeIcon icon={faCircleCheck} className="mr-status-icon" /> {t('services:medicineReminder.completed')}</>
                )}
              </div>

              <div className="mr-reminder-card-content">
                <div className="mr-reminder-card-header">
                  <div className="mr-medicine-icon-wrapper">
                    <FontAwesomeIcon icon={getMedicineTypeIcon(reminder.medicine_type)} className="mr-medicine-icon" />
                  </div>
                  <div className="mr-medicine-info">
                    <h3 className="mr-medicine-name">{reminder.medicine_name}</h3>
                    <span className="mr-medicine-type-badge">
                      {reminder.medicine_type}
                    </span>
                  </div>
                </div>

                <div className="mr-reminder-details-grid">
                  <div className="mr-detail-item">
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-detail-icon" />
                    <span className="mr-detail-text">
                      <strong>{reminder.dosage_value}</strong> {reminder.dosage_unit}
                    </span>
                  </div>
                  <div className="mr-detail-item">
                    <FontAwesomeIcon icon={faCalendarDays} className="mr-detail-icon" />
                    <span className="mr-detail-text">
                      {new Date(reminder.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(reminder.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  {reminder.doctor_name && (
                    <div className="mr-detail-item">
                      <FontAwesomeIcon icon={faUserDoctor} className="mr-detail-icon" />
                      <span className="mr-detail-text">{reminder.doctor_name}</span>
                    </div>
                  )}
                  <div className="mr-detail-item">
                    <FontAwesomeIcon icon={faClock} className="mr-detail-icon" />
                    <span className="mr-detail-text">
                      {reminder.intake_frequency?.replace('_', ' ') || 'once'} {t('services:medicineReminder.daily')}
                    </span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mr-quick-actions">
                  {status === 'active' && (
                    <button className="mr-quick-action-btn mr-quick-taken" title={t('services:medicineReminder.markTaken')}>
                      <FontAwesomeIcon icon={faCheckCircle} /> {t('services:medicineReminder.taken')}
                    </button>
                  )}
                  <button className="mr-quick-action-btn mr-quick-info" title={t('services:medicineReminder.schedule')}>
                    <FontAwesomeIcon icon={faClipboardCheck} /> {t('services:medicineReminder.schedule')}
                  </button>
                </div>
              </div>

              <div className="mr-reminder-card-actions">
                <button
                  className="mr-action-btn mr-edit-btn"
                  onClick={() => handleEditReminder(reminder)}
                  title={t('common:buttons.edit')}
                >
                  <FontAwesomeIcon icon={faEdit} /> {t('common:buttons.edit')}
                </button>
                <button
                  className="mr-action-btn mr-delete-btn"
                  onClick={() => setConfirmDelete(reminder.id)}
                  title={t('common:buttons.delete')}
                >
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render form
  // Render the scan prescription section
  const renderScanSection = () => (
    <div className="mr-scan-section">
      <div
        className="mr-scan-header"
        onClick={() => setScanExpanded(prev => !prev)}
      >
        <span className="mr-scan-header-title">
          <FontAwesomeIcon icon={faWandMagicSparkles} className="mr-scan-header-icon" />
          {t('services:medicineReminder.scanPrescription')}
        </span>
        <FontAwesomeIcon
          icon={scanExpanded ? faChevronUp : faChevronDown}
          className="mr-scan-toggle-icon"
        />
      </div>

      {scanExpanded && (
        <div className="mr-scan-body">
          <p className="mr-scan-hint">
            {t('services:medicineReminder.scanHint')}
          </p>

          {/* Idle — Upload area */}
          {scanStep === 'idle' && (
            <label className="mr-scan-upload-area">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handlePrescriptionUpload}
                hidden
              />
              <div className="mr-scan-upload-icon">
                <FontAwesomeIcon icon={faCloudArrowUp} />
              </div>
              <span className="mr-scan-upload-text">{t('services:medicineReminder.clickToUpload')}</span>
              <span className="mr-scan-upload-formats">{t('services:medicineReminder.uploadFormats')}</span>
            </label>
          )}

          {/* Scanning — progress bar */}
          {scanStep === 'scanning' && (
            <div className="mr-scan-progress">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-scan-spinner" />
              <div className="mr-scan-progress-info">
                <span className="mr-scan-progress-label">{t('services:medicineReminder.scanningDocument')}</span>
                <div className="mr-scan-progress-bar">
                  <div
                    className="mr-scan-progress-bar-fill"
                    style={{ width: `${Math.round(scanProgress)}%` }}
                  />
                </div>
                <span className="mr-scan-progress-percent">{t('services:medicineReminder.percentComplete', { percent: Math.round(scanProgress) })}</span>
              </div>
            </div>
          )}

          {/* Extracting — AI reading */}
          {scanStep === 'extracting' && (
            <div className="mr-scan-progress">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-scan-spinner" />
              <div className="mr-scan-progress-info">
                <span className="mr-scan-progress-label">{t('services:medicineReminder.aiReading')}</span>
                <span className="mr-scan-progress-percent">{t('services:medicineReminder.mayTakeSeconds')}</span>
              </div>
            </div>
          )}

          {/* Error */}
          {scanStep === 'error' && (
            <div className="mr-scan-error">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-scan-error-icon" />
              <div className="mr-scan-error-info">
                <span className="mr-scan-error-title">{t('services:medicineReminder.extractionFailed')}</span>
                <span className="mr-scan-error-hint">{scanError}</span>
              </div>
              <button type="button" className="mr-btn mr-btn-cancel" onClick={resetScan}>
                {t('common:buttons.tryAgain')}
              </button>
            </div>
          )}

          {/* Done — Medicine selection cards */}
          {scanStep === 'done' && extractedMedicines.length > 0 && (
            <div className="mr-scan-results">
              <div className="mr-scan-results-header">
                <div className="mr-scan-results-title">
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-scan-success-icon" />
                  <span dangerouslySetInnerHTML={{ __html: t('services:medicineReminder.foundMedicines', { count: extractedMedicines.length, plural: extractedMedicines.length > 1 ? 's' : '' }) }} />
                </div>
                <button type="button" className="mr-scan-reset-btn" onClick={resetScan}>
                  {t('services:medicineReminder.scanAnother')}
                </button>
              </div>

              {extractedDoctorName && (
                <div className="mr-scan-doctor-badge">
                  <FontAwesomeIcon icon={faUserDoctor} />
                  <span>{t('services:medicineReminder.prescribedBy')} <strong>{extractedDoctorName}</strong></span>
                </div>
              )}

              <div className="mr-scan-medicine-list">
                {extractedMedicines.map((med, index) => {
                  const isSelected = selectedMedicineIndices.has(index);
                  const isInfoExpanded = expandedDrugInfo.has(index);

                  return (
                    <div
                      key={index}
                      className={`mr-scan-med-card ${isSelected ? 'mr-scan-med-selected' : ''}`}
                    >
                      <div className="mr-scan-med-top" onClick={() => toggleMedicineSelection(index)}>
                        <div className="mr-scan-med-checkbox">
                          {isSelected ? (
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-scan-check-active" />
                          ) : (
                            <div className="mr-scan-check-empty" />
                          )}
                        </div>
                        <div className="mr-scan-med-info">
                          <div className="mr-scan-med-name-row">
                            <span className="mr-scan-med-name">{med.medicine_name}</span>
                            <span className="mr-scan-med-type-badge">
                              <FontAwesomeIcon icon={getMedicineTypeIcon(med.medicine_type)} style={{ marginRight: 4 }} />
                              {med.medicine_type}
                            </span>
                          </div>
                          <div className="mr-scan-med-details">
                            <span className="mr-scan-med-detail">
                              <FontAwesomeIcon icon={faBoxOpen} className="mr-scan-detail-icon" />
                              {med.dosage_value} {med.dosage_unit}, {med.frequency}
                            </span>
                            <span className="mr-scan-med-detail">
                              <FontAwesomeIcon icon={faCalendarDays} className="mr-scan-detail-icon" />
                              {med.duration_value} {med.duration_unit}{med.duration_value > 1 ? 's' : ''}
                            </span>
                            <span className="mr-scan-med-detail">
                              <FontAwesomeIcon icon={faClock} className="mr-scan-detail-icon" />
                              {med.meal_relation.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Drug Info toggle */}
                      {med.drug_info && (
                        <>
                          <button
                            type="button"
                            className="mr-scan-drug-info-toggle"
                            onClick={(e) => { e.stopPropagation(); toggleDrugInfo(index); }}
                          >
                            <FontAwesomeIcon icon={faBookMedical} />
                            {t('services:medicineReminder.drugInformation')}
                            <FontAwesomeIcon icon={isInfoExpanded ? faChevronUp : faChevronDown} className="mr-scan-drug-chevron" />
                          </button>

                          {isInfoExpanded && (
                            <div className="mr-scan-drug-info-panel">
                              {med.drug_info.used_for && (
                                <div className="mr-scan-drug-row">
                                  <span className="mr-scan-drug-label">{t('services:medicineReminder.usedFor')}</span>
                                  <span className="mr-scan-drug-value">{med.drug_info.used_for}</span>
                                </div>
                              )}
                              {med.drug_info.common_side_effects && med.drug_info.common_side_effects.length > 0 && (
                                <div className="mr-scan-drug-row">
                                  <span className="mr-scan-drug-label">{t('services:medicineReminder.sideEffects')}</span>
                                  <span className="mr-scan-drug-value">{med.drug_info.common_side_effects.join(', ')}</span>
                                </div>
                              )}
                              {med.drug_info.important_warnings && med.drug_info.important_warnings.length > 0 && (
                                <div className="mr-scan-drug-row mr-scan-drug-warning">
                                  <span className="mr-scan-drug-label">
                                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 4 }} />
                                    {t('services:medicineReminder.warnings')}
                                  </span>
                                  <span className="mr-scan-drug-value">{med.drug_info.important_warnings.join('; ')}</span>
                                </div>
                              )}
                              {med.drug_info.how_to_take && (
                                <div className="mr-scan-drug-row">
                                  <span className="mr-scan-drug-label">{t('services:medicineReminder.howToTake')}</span>
                                  <span className="mr-scan-drug-value">{med.drug_info.how_to_take}</span>
                                </div>
                              )}
                              {med.drug_info.interactions && med.drug_info.interactions.length > 0 && (
                                <div className="mr-scan-drug-row">
                                  <span className="mr-scan-drug-label">{t('services:medicineReminder.interactions')}</span>
                                  <span className="mr-scan-drug-value">{med.drug_info.interactions.join(', ')}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action buttons */}
              <div className="mr-scan-actions">
                <button
                  type="button"
                  className="mr-btn mr-btn-primary"
                  onClick={handleAddSelectedMedicine}
                  disabled={selectedMedicineIndices.size === 0}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  {t('services:medicineReminder.addSelected', { count: selectedMedicineIndices.size })}
                </button>
                <button
                  type="button"
                  className="mr-btn mr-scan-btn-all"
                  onClick={() => setShowAddAllConfirm(true)}
                >
                  <FontAwesomeIcon icon={faCheckDouble} />
                  {t('services:medicineReminder.addAllAsReminders')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAddForm = () => (
    <div className="mr-form-section">

      {/* Auto-filled banner */}
      {autoFilledFromScan && (
        <div className="mr-scan-autofill-banner">
          <FontAwesomeIcon icon={faCircleInfo} className="mr-scan-autofill-icon" />
          <span>{t('services:medicineReminder.autoFilledBanner')}</span>
          <button
            type="button"
            className="mr-scan-autofill-dismiss"
            onClick={() => setAutoFilledFromScan(false)}
          >
            {t('common:buttons.dismiss')}
          </button>
        </div>
      )}

      {/* Scan Prescription Section — only in create mode */}
      {!editingReminder && renderScanSection()}

      <div className="mr-form-card-new">

        {/* Row 1: Medicine Name + Type */}
        <div className="mr-field-row mr-field-row-2">
          <div className="mr-field">
            <label>{t('services:medicineReminder.medicineName')} <span className="mr-required">*</span></label>
            <input
              type="text"
              placeholder="e.g. Amoxicillin"
              value={formData.medicineName}
              onChange={(e) => setFormData({ ...formData, medicineName: e.target.value })}
            />
          </div>
          <div className="mr-field">
            <label>{t('services:medicineReminder.medicineType')}</label>
            <div className="mr-pill-group">
              {getMedicineTypeOptions(t).map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={`mr-pill-btn ${formData.medicineType === type.value ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, medicineType: type.value as MedicineType })}
                >
                  <FontAwesomeIcon icon={type.icon} /> {type.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2: Dosage + Meal Relation */}
        <div className="mr-field-row mr-field-row-2">
          <div className="mr-field">
            <label>{t('services:medicineReminder.dosage')}</label>
            <div className="mr-dosage-combined">
              <input
                type="number"
                min="1"
                value={formData.dosageValue}
                onChange={(e) => setFormData({ ...formData, dosageValue: parseInt(e.target.value) || 1 })}
                placeholder="e.g. 1"
              />
              <select
                value={formData.dosageUnit}
                onChange={(e) => setFormData({ ...formData, dosageUnit: e.target.value as DosageUnit })}
              >
                <option value="tablet">Tablet(s)</option>
                <option value="tablespoons">Tablespoon(s)</option>
                <option value="drops">Drop(s)</option>
                <option value="as_directed">As Directed</option>
              </select>
            </div>
          </div>
          <div className="mr-field">
            <label>Meal Relation</label>
            <div className="mr-pill-group">
              {([
                { value: 'before_meal', label: 'Before Meal' },
                { value: 'after_meal', label: 'After Meal' },
                { value: 'with_meal', label: 'With Meal' },
              ] as { value: MealRelation; label: string }[]).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`mr-pill-btn ${defaultMealRelation === option.value ? 'active' : ''}`}
                  onClick={() => handleDefaultMealRelationChange(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Frequency (pill buttons) */}
        <div className="mr-field-row">
          <div className="mr-field">
            <label>How often?</label>
            <div className="mr-pill-group">
              {([
                { value: 'once', label: 'Once daily' },
                { value: 'twice', label: 'Twice daily' },
                { value: 'thrice', label: '3 times daily' },
                { value: 'four_times', label: '4 times daily' },
              ] as { value: IntakeFrequency; label: string }[]).map((freq) => (
                <button
                  key={freq.value}
                  type="button"
                  className={`mr-pill-btn ${formData.intakeFrequency === freq.value ? 'active' : ''}`}
                  onClick={() => handleIntakeFrequencyChange(freq.value)}
                >
                  {freq.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4: Schedule Times */}
        <div className="mr-field-row">
          <div className="mr-field">
            <label>
              <FontAwesomeIcon icon={faClock} style={{ marginRight: 6 }} />
              Schedule Times
            </label>
            <div className="mr-schedule-grid">
              {formData.scheduleTimes.map((slot, index) => (
                <div className="mr-schedule-chip" key={index}>
                  <span className="mr-schedule-num">{index + 1}</span>
                  <input
                    type="time"
                    value={slot.time}
                    onChange={(e) => handleScheduleTimeChange(index, 'time', e.target.value)}
                  />
                  <select
                    value={slot.meal_relation}
                    onChange={(e) => handleScheduleTimeChange(index, 'meal_relation', e.target.value)}
                  >
                    <option value="before_meal">Before Meal</option>
                    <option value="after_meal">After Meal</option>
                    <option value="with_meal">With Meal</option>
                    <option value="none">No Relation</option>
                  </select>
                  {formData.scheduleTimes.length > 1 && (
                    <button
                      type="button"
                      className="mr-schedule-chip-remove"
                      onClick={() => handleRemoveTimeSlot(index)}
                      title="Remove time slot"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="mr-schedule-add-chip"
                onClick={handleAddTimeSlot}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Time Slot
              </button>
            </div>
          </div>
        </div>

        {/* Row 5: Duration + Dates */}
        <div className="mr-field-row mr-field-row-3">
          <div className="mr-field">
            <label>Duration</label>
            <div className="mr-duration-combined">
              <input
                type="number"
                min="1"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 1 })}
                placeholder="e.g. 7"
              />
              <select
                value={formData.durationUnit}
                onChange={(e) => setFormData({ ...formData, durationUnit: e.target.value as 'day' | 'week' | 'month' })}
              >
                <option value="day">Days</option>
                <option value="week">Weeks</option>
                <option value="month">Months</option>
              </select>
            </div>
          </div>
          <div className="mr-field">
            <label>Start Date <span className="mr-required">*</span></label>
            <input
              type="date"
              value={formData.startDate}
              min={editingReminder ? undefined : new Date().toISOString().split('T')[0]}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
          </div>
          <div className="mr-field">
            <label>End Date <span className="mr-required">*</span></label>
            <input
              type="date"
              value={formData.endDate}
              min={formData.startDate || (editingReminder ? undefined : new Date().toISOString().split('T')[0])}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
          </div>
        </div>

        {/* Row 6: Doctor's Name */}
        <div className="mr-field-row">
          <div className="mr-field">
            <label>Doctor's Name</label>
            <input
              type="text"
              placeholder="Dr. Name (optional)"
              value={formData.doctorName}
              onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
            />
          </div>
        </div>

        {/* Advanced Options (collapsible) */}
        <div
          className="mr-advanced-toggle"
          onClick={() => setAdvancedExpanded(prev => !prev)}
        >
          <span>Advanced Options</span>
          <FontAwesomeIcon icon={advancedExpanded ? faChevronUp : faChevronDown} />
        </div>
        {advancedExpanded && (
          <div className="mr-advanced-section">
            <div className="mr-field-row mr-field-row-3">
              <div className="mr-field">
                <label>Appointment Reminder</label>
                <input
                  type="date"
                  value={formData.appointmentReminderDate}
                  onChange={(e) => setFormData({ ...formData, appointmentReminderDate: e.target.value })}
                />
              </div>
              <div className="mr-field">
                <label>Current Inventory</label>
                <input
                  type="number"
                  placeholder="e.g. 30"
                  value={formData.currentInventory || ''}
                  onChange={(e) => setFormData({ ...formData, currentInventory: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="mr-field">
                <label>Low Stock Alert</label>
                <input
                  type="number"
                  placeholder="e.g. 5"
                  value={formData.remindWhenInventory || ''}
                  onChange={(e) => setFormData({ ...formData, remindWhenInventory: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Form Buttons */}
        <div className="mr-form-actions">
          <button
            type="button"
            className="mr-btn mr-btn-primary"
            onClick={handleSaveReminder}
            disabled={savingReminder}
          >
            {savingReminder ? 'Saving...' : editingReminder ? 'Update Reminder' : 'Save Reminder'}
          </button>
          <button
            type="button"
            className="mr-btn mr-btn-cancel"
            onClick={() => {
              resetForm();
              setActiveTab('all');
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mr-page">
      {/* Toast Notification */}
      {toast && (
        <div className="mr-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete !== null && (
        <div className="mr-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="mr-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mr-confirm-modal-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h3>Delete Reminder?</h3>
            <p>Are you sure you want to delete this reminder? This action cannot be undone.</p>
            <div className="mr-confirm-modal-actions">
              <button className="mr-btn mr-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="mr-btn mr-btn-danger" onClick={() => handleDeleteReminder(confirmDelete)}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add All Confirmation Modal */}
      {showAddAllConfirm && (
        <div className="mr-modal-overlay" onClick={() => !addingAllReminders && setShowAddAllConfirm(false)}>
          <div className="mr-confirm-modal mr-scan-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mr-confirm-modal-icon" style={{ color: '#06b6d4' }}>
              <FontAwesomeIcon icon={faCheckDouble} />
            </div>
            <h3>Add All as Reminders?</h3>
            <p>
              This will create {selectedMedicineIndices.size > 0 ? selectedMedicineIndices.size : extractedMedicines.length} medicine reminder{(selectedMedicineIndices.size > 0 ? selectedMedicineIndices.size : extractedMedicines.length) > 1 ? 's' : ''}:
            </p>
            <div className="mr-scan-confirm-list">
              {(selectedMedicineIndices.size > 0
                ? Array.from(selectedMedicineIndices).map(i => extractedMedicines[i])
                : extractedMedicines
              ).map((med, idx) => (
                <div key={idx} className="mr-scan-confirm-item">
                  <FontAwesomeIcon icon={getMedicineTypeIcon(med.medicine_type)} className="mr-scan-confirm-item-icon" />
                  <span className="mr-scan-confirm-item-name">{med.medicine_name}</span>
                  <span className="mr-scan-confirm-item-detail">
                    {med.dosage_value} {med.dosage_unit}, {med.frequency}
                  </span>
                </div>
              ))}
            </div>
            <div className="mr-confirm-modal-actions">
              <button
                className="mr-btn mr-btn-cancel"
                onClick={() => setShowAddAllConfirm(false)}
                disabled={addingAllReminders}
              >
                Cancel
              </button>
              <button
                className="mr-btn mr-btn-primary"
                onClick={handleAddAllAsReminders}
                disabled={addingAllReminders}
              >
                {addingAllReminders ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Creating...</>
                ) : (
                  <><FontAwesomeIcon icon={faCheckDouble} /> Confirm & Add All</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ WELCOME BANNER ============ */}
      <section className="mr-welcome-banner">
        <Container>
          <BackButton />
          <div className="mr-welcome-layout">
            <div className="mr-welcome-text">
              <h1 className="mr-welcome-title">
                <FontAwesomeIcon icon={faCapsules} />
                Medicine Reminders
              </h1>
              <p className="mr-welcome-subtitle">
                Never miss a dose. Track your medications, set reminders, and manage your health regimen all in one place.
              </p>
            </div>
            <div className="mr-welcome-actions">
              <button
                className="mr-welcome-btn primary"
                onClick={() => { resetForm(); setActiveTab('add'); }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Reminder
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* ============ STATS ROW ============ */}
      <section className="mr-stats-section">
        <Container>
          <div className="mr-stats-row">
            {statItems.map((item, idx) => (
              <div className="mr-stat-card" key={idx}>
                <div className="mr-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="mr-stat-info">
                  <span className="mr-stat-value">{item.value}</span>
                  <span className="mr-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Main Content */}
      <Container>
        {/* Tab Navigation */}
        <div className="mr-tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`mr-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => {
                if (tab.key === 'add') resetForm();
                setActiveTab(tab.key);
              }}
            >
              <FontAwesomeIcon icon={tab.icon} className="mr-tab-icon" />
              {tab.label}
              {tab.count !== undefined && (
                <span className="mr-tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mr-tab-content">
          {/* Section Header */}
          <div className={`mr-tab-header ${currentHeader.className}`}>
            <div className="mr-tab-header-text">
              <h2>
                <FontAwesomeIcon icon={currentHeader.icon} className="mr-tab-header-icon" />
                {currentHeader.title}
              </h2>
              <p>{currentHeader.desc}</p>
            </div>
            {activeTab !== 'add' && (
              <button
                className="mr-btn mr-btn-primary mr-add-btn"
                onClick={() => { resetForm(); setActiveTab('add'); }}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Reminder
              </button>
            )}
          </div>

          {/* Reminder list tabs */}
          {activeTab !== 'add' && renderReminderCards()}

          {/* Add/Edit form tab */}
          {activeTab === 'add' && renderAddForm()}
        </div>
      </Container>
    </div>
  );
};

export default MedicineReminder;
